// SetPassword (NEW DESIGN — visual revamp only). Logic verbatim copy of
// SetPassword.js. Differences:
//   - LoginNewWrapper instead of LoginWrapper (imported from sibling Login folder).
//   - "Go to Login" CTA and post-set-password fallback redirect → canonical /login.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, Result, notification } from 'antd';
import { useDispatch } from 'react-redux';
import LottieLoader from '../../Components/common/LottieLoader';
import { ThemeForm, ThemeButton } from '../Styles/Theme';
import { LoginNewWrapper } from '../Login/LoginNewStyle';
import LoginLeftPanel from '../Login/LoginLeftPanel';
import { validateSession, setPassword } from '../../services/invitation.service';
import { STORAGE_KEYS } from '../../utils/constants';
import { setPermissions } from '../../store/actions/permissionsAction';
import { checkModulePermissions, getFirstAvailableRoute } from '../../utils/helpers';
import { landOnToplineOr } from '../../store/actions/projectAction';

const SetPasswordNew = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form] = ThemeForm.useForm();
	const [status, setStatus] = useState('loading');
	const [message, setMessage] = useState('');
	const [errorCode, setErrorCode] = useState(null);
	const [userName, setUserName] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
		const pendingUser = localStorage.getItem(STORAGE_KEYS.PENDING_USER);

		if (!sessionToken) {
			setStatus('error');
			setMessage('Invalid session. Please use the invitation link from your email.');
			setErrorCode('INVALID_SESSION');
			return;
		}

		if (pendingUser) {
			try {
				const user = JSON.parse(pendingUser);
				setUserName(user.name || '');
			} catch (e) {
				// Ignore parse errors
			}
		}

		const validate = async () => {
			const result = await validateSession(sessionToken);

			if (result.success) {
				setStatus('ready');
				setUserName(result.data?.user?.name || userName);
			} else {
				setStatus('error');
				setMessage(result.message);
				setErrorCode(result.data?.code || 'SESSION_EXPIRED');

				localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
				localStorage.removeItem(STORAGE_KEYS.PENDING_USER);
			}
		};

		validate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSubmit = async (values) => {
		const sessionToken = localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);

		if (!sessionToken) {
			notification.error({
				message: 'Session Expired',
				description: 'Please use the invitation link from your email again.',
			});
			return;
		}

		setLoading(true);

		const result = await setPassword(sessionToken, values.password, values.confirmPassword);

		setLoading(false);

		if (result.success) {
			localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
			localStorage.removeItem(STORAGE_KEYS.PENDING_USER);

			const data = result.data || {};

			if (data.mode === 'logged-in' && data.accessToken && data.refreshToken) {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
				localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
				localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data.user));
				if (data.permissions) {
					localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(data.permissions));
					dispatch(setPermissions(data.permissions));
				}
				if (data.appVersion) {
					localStorage.setItem('app_version', data.appVersion);
				}
				notification.success({
					message: 'Welcome',
					description: 'Your password has been set. You are now signed in.',
				});
				// Topline first when there is a summary on it to see (client
				// 2026-09-28), ahead of the per-org chat redirect below.
				landOnToplineOr(() => navigate('/topline', { replace: true }), () => {
					// Per-org login redirect: backend sets redirect_chat_id when the
					// user's org has chat_redirection_enabled. Take that path before
					// the normal landing-page fallback so the user lands directly in
					// their fresh "New Insights" chat.
					if (data?.redirect_chat_id) {
						navigate(`/insight-engine/${data.redirect_chat_id}`);
						return;
					}
					const dashboardPermission = checkModulePermissions('dashboard');
					if (dashboardPermission.authorized) {
						navigate('/', { replace: true });
					} else {
						const first = getFirstAvailableRoute();
						navigate(first ? first.path : '/', { replace: true });
					}
				});
				return;
			}

			notification.success({
				message: 'Success',
				description: 'Your password has been set successfully. You can now login.',
			});
			navigate('/login', { replace: true });
		} else {
			notification.error({
				message: 'Error',
				description: result.message,
			});
		}
	};

	const footer = (
		<div className='loginFooter'>
			<span>@{new Date().getFullYear()} CrowdVox Inc.</span>
		</div>
	);

	if (status === 'loading') {
		return (
			<LoginNewWrapper>
				<Row gutter={[20, 16]}>
					<Col
						xs={{ span: 24, order: 1 }}
						sm={{ span: 24, order: 1 }}
						md={{ span: 12, order: 2 }}
						lg={{ span: 14, order: 2 }}
						xl={{ span: 14, order: 2 }}
					>
						<div className='loginRightCard'>
							<div className='loginLogo'>
								<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
							</div>
							<div className='loginContainer'>
								<div className='loginStatusBlock'>
									<LottieLoader size='large' />
									<h1>Validating Session...</h1>
									<p>Please wait while we validate your session.</p>
								</div>
							</div>
							{footer}
						</div>
					</Col>
					<Col
						xs={{ span: 0, order: 2 }}
						sm={{ span: 0, order: 2 }}
						md={{ span: 12, order: 1 }}
						lg={{ span: 10, order: 1 }}
						xl={{ span: 10, order: 1 }}
					>
						<LoginLeftPanel />
					</Col>
				</Row>
			</LoginNewWrapper>
		);
	}

	if (status === 'error') {
		const getErrorTitle = () => {
			switch (errorCode) {
				case 'ALREADY_ACTIVE':
					return 'Account Already Active';
				case 'SESSION_EXPIRED':
				case 'INVALID_SESSION':
					return 'Link Expired or Invalid';
				default:
					return 'Session Invalid';
			}
		};

		const getErrorMessage = () => {
			switch (errorCode) {
				case 'ALREADY_ACTIVE':
					return 'Your account is already set up. You can login with your credentials.';
				case 'SESSION_EXPIRED':
				case 'INVALID_SESSION':
					return 'This invitation link has expired or has already been used. Please contact your administrator to receive a new invitation.';
				default:
					return message || 'Something went wrong. Please contact your administrator for assistance.';
			}
		};

		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '100vh',
				padding: '20px',
				backgroundColor: '#f5f5f5',
			}}>
				<Result
					status={errorCode === 'ALREADY_ACTIVE' ? 'info' : 'error'}
					title={getErrorTitle()}
					subTitle={getErrorMessage()}
					extra={
						<ThemeButton size='large' className='loginBtn' onClick={() => navigate('/login', { replace: true })}>
							Go to Login
						</ThemeButton>
					}
				/>
			</div>
		);
	}

	return (
		<LoginNewWrapper>
			<Row gutter={[20, 16]}>
				<Col
					xs={{ span: 24, order: 1 }}
					sm={{ span: 24, order: 1 }}
					md={{ span: 12, order: 2 }}
					lg={{ span: 14, order: 2 }}
					xl={{ span: 14, order: 2 }}
				>
					<div className='loginRightCard'>
						<div className='loginLogo'>
							<img src='/images/login-new/crowdvox-logo.svg' alt='CrowdVox' />
						</div>

						<div className='loginContainer'>
							<h1>Set Your Password</h1>
							{userName ? (
								<p>Welcome, <strong>{userName}</strong>! Please set your password to continue.</p>
							) : (
								<p>Please set your password to continue.</p>
							)}

							<ThemeForm
								form={form}
								name='setPasswordFormNew'
								layout='vertical'
								autoComplete='off'
								onFinish={handleSubmit}
								className='loginFormV2'
							>
								<ThemeForm.Item
									name='password'
									label='New Password'
									rules={[
										{ required: true, message: 'Please enter your password' },
										{ min: 8, message: 'Password must be at least 8 characters' },
										{ max: 20, message: 'Password must not exceed 20 characters' },
										{ pattern: /^[A-Za-z\d@$!%*?&]*$/, message: 'Only letters, numbers and @$!%*?& special characters are allowed' },
										{ pattern: /[A-Z]/, message: 'Password must contain at least one uppercase letter' },
										{ pattern: /[a-z]/, message: 'Password must contain at least one lowercase letter' },
										{ pattern: /\d/, message: 'Password must contain at least one number' },
										{ pattern: /[@$!%*?&]/, message: 'Password must contain at least one special character (@$!%*?&)' },
									]}
								>
									<Input.Password
										prefix={<img src='/images/login-new/icon-lock.svg' alt='' />}
										size='large'
										placeholder='Enter new password'
										autoComplete='new-password'
									/>
								</ThemeForm.Item>

								<ThemeForm.Item
									name='confirmPassword'
									label='Confirm Password'
									dependencies={['password']}
									rules={[
										{ required: true, message: 'Please confirm your password' },
										({ getFieldValue }) => ({
											validator(_, value) {
												if (!value || getFieldValue('password') === value) {
													return Promise.resolve();
												}
												return Promise.reject(new Error('Passwords do not match'));
											},
										}),
									]}
								>
									<Input.Password
										prefix={<img src='/images/login-new/icon-lock.svg' alt='' />}
										size='large'
										placeholder='Confirm new password'
										autoComplete='new-password'
									/>
								</ThemeForm.Item>

								<ThemeForm.Item style={{ marginBottom: 0, marginTop: 8 }}>
									{loading ? (
										<ThemeButton size='large' loading className='loginBtn'>
											Setting Password...
										</ThemeButton>
									) : (
										<ThemeButton size='large' className='loginBtn' htmlType='submit'>
											Set Password
										</ThemeButton>
									)}
								</ThemeForm.Item>
							</ThemeForm>
						</div>

						{footer}
					</div>
				</Col>

				<Col
					xs={{ span: 0, order: 2 }}
					sm={{ span: 0, order: 2 }}
					md={{ span: 12, order: 1 }}
					lg={{ span: 10, order: 1 }}
					xl={{ span: 10, order: 1 }}
				>
					<LoginLeftPanel />
				</Col>
			</Row>
		</LoginNewWrapper>
	);
};

export default SetPasswordNew;
